// GELÉ le 02/10/2026. Version fluide = commit 17009819.
// Interdit d'y retoucher : ni préchauffage, ni currentTime, ni mesure dans le chemin de lecture.
// GEL BÊTA 07/10 — docs/GEL_BETA.md. Porte ouverte aux retours Hiti, sans toucher la logique lecteur.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Video } from "@/types/videos";
import {
  deckEngine,
  noteDeckDirection,
  pauseDeck,
  prepareDeckNeighbour,
  shouldWarmBothNeighbours,
  unlockDeckElements,
  warmDeckAhead,
} from "@/services/playback/deckEngine";
import { warmWindow } from "@/services/playback/feedUrls";
import { PlaybackEngineContext } from "@/services/playback/engineContext";

type FeedItem = Video | { id: string; isAd: true };

interface FeedDeckProps {
  videos: FeedItem[];
  initialIndex: number;
  onIndexChange?: (index: number) => void;
  renderVideo: (
    video: FeedItem,
    index: number,
    isActive: boolean,
    scrollToNext: () => void,
    totalVideos: number,
  ) => ReactNode;
  containerClassName?: string;
  videoClassName?: string;
  isScrollLocked?: boolean;
  showBackground?: boolean;
}

/** Les trois cartes du deck. Ces clés ne changent JAMAIS. */
const CARDS = [0, 1, 2] as const;
/** Position initiale de chaque carte, relative à la vidéo courante. */
const INITIAL_OFFSETS = [-1, 0, 1];

/** Part de la hauteur au-delà de laquelle un glissement lent change de vidéo. */
const DISTANCE_RATIO = 0.45;
/** Vitesse (px/ms) au-delà de laquelle un geste court suffit. */
const VELOCITY_THRESHOLD = 0.35;
/** Distance minimale pour qu'un geste rapide compte. */
const VELOCITY_MIN_DISTANCE = 24;
/** Seuil d'engagement du geste, en pixels. */
const ENGAGE_PX = 8;
/** Durée de l'animation de recalage, en millisecondes. */
const SETTLE_MS = 260;
/** Molette : cumul nécessaire pour avancer d'une vidéo. */
const WHEEL_THRESHOLD = 120;
/** Molette : silence requis avant d'accepter un nouveau cran. */
const WHEEL_COOLDOWN_MS = 420;
/** Résistance appliquée au glissement en début et en fin de liste. */
const EDGE_RESISTANCE = 0.25;

/**
 * Deck vertical à trois cartes.
 *
 * Les trois cartes sont permanentes, ce sont les vidéos qui tournent au travers.
 * Chaque carte possède son propre `<video>`, jamais déplacé ni remonté : au
 * geste, les trois cartes glissent ensemble et l'image de la vidéo qui arrive
 * est déjà décodée, donc pas de carte vide ni de frame noire.
 *
 * Les URLs signées sont préparées par fenêtre (`warmWindow`) : au moment du
 * geste, il n'y a plus aucun aller-retour serveur à faire.
 */
const FeedDeck = ({
  videos,
  initialIndex,
  onIndexChange,
  renderVideo,
  containerClassName = "",
  videoClassName = "",
  isScrollLocked = false,
  showBackground = true,
}: FeedDeckProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [offsets, setOffsets] = useState<number[]>(INITIAL_OFFSETS);

  /** Décalage commun aux trois cartes : doigt en cours, puis recalage animé. */
  const [trackY, setTrackY] = useState(0);
  const [animating, setAnimating] = useState(false);

  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  /*
   * Miroirs lus PENDANT le geste, avant que React n'ait re-rendu : la lecture
   * doit démarrer dans le geste, donc sans attendre le rendu suivant.
   */
  const offsetsRef = useRef(offsets);
  offsetsRef.current = offsets;
  const videosRef = useRef(videos);
  videosRef.current = videos;
  /** Un geste à la fois : verrou levé à la fin du recalage. */
  const busyRef = useRef(false);
  const settleTimer = useRef<number | null>(null);

  /* ---------------------------------------------------------------- */
  /* Hauteur d'une carte                                               */
  /* ---------------------------------------------------------------- */

  /*
   * Mesurée dans le DOM, jamais déduite de `window.innerHeight` : sur iOS la
   * barre d'adresse rétractable désynchronise les deux, et la rotation change
   * la hauteur sans démonter le composant.
   */
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const next = containerRef.current?.clientHeight ?? 0;
      if (next > 0) setHeight(next);
    };
    measure();

    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener("orientationchange", measure);
    window.visualViewport?.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("orientationchange", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, []);

  /* ---------------------------------------------------------------- */
  /* Changement de vidéo                                               */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    onIndexChange?.(currentIndex);
  }, [currentIndex, onIndexChange]);

  /**
   * Fait tourner l'anneau des décalages. La carte qui sortait par le haut
   * réapparaît en bas (et inversement) : elle seule reçoit une autre vidéo, les
   * deux autres gardent la leur, donc leur image et leur flux.
   */
  const rotate = useCallback((step: 1 | -1) => {
    setOffsets((previous) =>
      previous.map((offset) => {
        const next = offset - step;
        if (next < -1) return 1;
        if (next > 1) return -1;
        return next;
      }),
    );
  }, []);

  /**
   * Démarre la vidéo visée DANS le geste, sans attendre le recalage : WebKit
   * refuse une lecture sonore qui ne part pas d'un geste utilisateur.
   */
  const activateTarget = useCallback((target: number, step: 1 | -1) => {
    const item = videosRef.current[target];
    if (!item) return;

    // Une publicité n'a pas d'élément vidéo : sans cette pause, le son de la
    // vidéo précédente continuerait par-dessus le panneau.
    if ("isAd" in item) {
      pauseDeck();
      return;
    }

    const card = CARDS.find((candidate) => offsetsRef.current[candidate] === step);
    if (card === undefined) return;
    const el = cardRefs.current[card]?.querySelector("video") ?? null;
    deckEngine.setActive(item as Video, el, target);
  }, []);

  const go = useCallback(
    (step: 1 | -1) => {
      if (busyRef.current || isScrollLocked || height === 0) return;
      const target = currentIndexRef.current + step;
      if (target < 0 || target >= videos.length) {
        // Bord de la liste : on ramène doucement les cartes en place.
        setAnimating(true);
        setTrackY(0);
        return;
      }

      busyRef.current = true;
      setAnimating(true);
      setTrackY(-step * height);

      noteDeckDirection(step);
      activateTarget(target, step);

      if (settleTimer.current) window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => {
        // Commit : l'anneau tourne et le décalage repart de zéro. Les cartes
        // retrouvent la position qu'elles occupaient déjà à l'écran.
        setAnimating(false);
        setTrackY(0);
        setCurrentIndex(target);
        rotate(step);
        busyRef.current = false;
        settleTimer.current = null;
      }, SETTLE_MS);
    },
    [activateTarget, height, isScrollLocked, rotate, videos.length],
  );

  const scrollToNext = useCallback(() => go(1), [go]);

  useEffect(
    () => () => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
      deckEngine.reset();
    },
    [],
  );

  /* ---------------------------------------------------------------- */
  /* Geste au pointeur : tactile, souris, stylet                       */
  /* ---------------------------------------------------------------- */

  const dragRef = useRef<{ id: number; startY: number; startAt: number; active: boolean } | null>(
    null,
  );

  /** Un geste parti d'une commande ne doit pas faire défiler le feed. */
  const isInteractiveTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    // Le bouton lecture/pause du lecteur couvre tout l'écran : il est marqué
    // explicitement comme transparent au geste, sinon aucun glissement ne
    // passerait sur la vidéo. Le tap simple continue de l'atteindre.
    if (target.closest("[data-swipe-passthrough]")) return false;
    return !!target.closest(
      'button, a, input, textarea, select, [role="slider"], [role="dialog"], [data-no-swipe]',
    );
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Geste réel, même sans défilement : c'est le seul moment où WebKit accorde
    // son autorisation de lecture aux cartes apparues après le premier geste.
    unlockDeckElements();
    if (isScrollLocked || busyRef.current || height === 0) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (isInteractiveTarget(event.target)) return;

    dragRef.current = {
      id: event.pointerId,
      startY: event.clientY,
      startAt: performance.now(),
      active: false,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;

    const delta = event.clientY - drag.startY;

    // Seuil d'engagement : en deçà, on laisse passer les taps et les gestes
    // horizontaux, sinon le moindre tremblement ferait bouger le feed.
    if (!drag.active) {
      if (Math.abs(delta) < ENGAGE_PX) return;
      drag.active = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setAnimating(false);
    }

    // Résistance aux extrémités : le feed cède, mais ne part pas dans le vide.
    const atStart = currentIndexRef.current === 0 && delta > 0;
    const atEnd = currentIndexRef.current === videos.length - 1 && delta < 0;
    setTrackY(atStart || atEnd ? delta * EDGE_RESISTANCE : delta);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    dragRef.current = null;

    if (!drag.active) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const delta = event.clientY - drag.startY;
    const duration = Math.max(1, performance.now() - drag.startAt);
    const velocity = Math.abs(delta) / duration;

    const farEnough = Math.abs(delta) > height * DISTANCE_RATIO;
    const fastEnough =
      velocity > VELOCITY_THRESHOLD && Math.abs(delta) > VELOCITY_MIN_DISTANCE;

    if (farEnough || fastEnough) {
      go(delta < 0 ? 1 : -1);
    } else {
      setAnimating(true);
      setTrackY(0);
    }
  };

  /* ---------------------------------------------------------------- */
  /* Molette et pavé tactile                                           */
  /* ---------------------------------------------------------------- */

  const wheelAccumulator = useRef(0);
  const lastWheelAt = useRef(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      if (isScrollLocked) return;
      event.preventDefault();

      const now = performance.now();
      // L'inertie d'un pavé tactile envoie des dizaines d'évènements après le
      // geste : sans ce silence, un seul mouvement sauterait plusieurs vidéos.
      if (now - lastWheelAt.current < WHEEL_COOLDOWN_MS) return;

      wheelAccumulator.current += event.deltaY;
      if (Math.abs(wheelAccumulator.current) < WHEEL_THRESHOLD) return;

      const step = wheelAccumulator.current > 0 ? 1 : -1;
      wheelAccumulator.current = 0;
      lastWheelAt.current = now;
      go(step);
    };

    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [go, isScrollLocked]);

  /* ---------------------------------------------------------------- */
  /* Clavier                                                           */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isScrollLocked) return;
      if (isInteractiveTarget(event.target)) return;

      if (event.key === "ArrowDown" || event.key === "PageDown") {
        event.preventDefault();
        unlockDeckElements();
        go(1);
      } else if (event.key === "ArrowUp" || event.key === "PageUp") {
        event.preventDefault();
        unlockDeckElements();
        go(-1);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [go, isScrollLocked]);

  /* ---------------------------------------------------------------- */
  /* Signature en lot et préparation des voisines                      */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (videos.length === 0) return;
    warmWindow(videos, currentIndex);
  }, [videos, currentIndex]);

  /*
   * Entrées qui ne passent pas par un geste (ouverture du feed, bouton
   * « Continuer ») : une publicité à l'écran doit couper tout son.
   */
  useEffect(() => {
    const item = videos[currentIndex];
    if (item && "isAd" in item) pauseDeck();
  }, [currentIndex, videos]);

  useEffect(() => {
    if (height === 0) return;
    // La suivante d'abord : c'est le sens du geste, elle a la priorité sur le
    // budget de flux. La précédente ne se sert que s'il reste de la place, et
    // seulement hors mode économie de données.
    const wanted = shouldWarmBothNeighbours() ? [1, -1] : [1];
    wanted.forEach((offset) => {
      const card = CARDS.find((candidate) => offsets[candidate] === offset);
      if (card === undefined) return;
      const index = currentIndex + offset;
      const item = videos[index];
      if (!item || "isAd" in item) return;
      const el = cardRefs.current[card]?.querySelector("video") ?? null;
      prepareDeckNeighbour(item as Video, el, index, offset === 1);
    });
    warmDeckAhead(videos, currentIndex);
  }, [currentIndex, offsets, videos, height]);

  /* ---------------------------------------------------------------- */
  /* Rendu                                                             */
  /* ---------------------------------------------------------------- */

  return (
    <PlaybackEngineContext.Provider value={deckEngine}>
      <div
        ref={containerRef}
        className={`relative h-full w-full overflow-hidden ${
          showBackground ? "bg-player-background" : ""
        } ${containerClassName}`}
        style={{
          // Le navigateur ne doit pas s'approprier le geste vertical, sauf
          // quand un panneau est ouvert : il doit alors pouvoir défiler.
          touchAction: isScrollLocked ? "auto" : "none",
          overscrollBehavior: "contain",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {CARDS.map((card) => {
          const offset = offsets[card];
          const index = currentIndex + offset;
          const item = videos[index];
          const isActive = offset === 0;

          return (
            <div
              key={card}
              ref={(node) => {
                cardRefs.current[card] = node;
              }}
              className="absolute inset-0 w-full"
              style={{
                transform: `translate3d(0, ${offset * height + trackY}px, 0)`,
                transition: animating
                  ? `transform ${SETTLE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
                willChange: "transform",
                // Seule la carte à l'écran reçoit les clics.
                pointerEvents: isActive ? "auto" : "none",
              }}
              aria-hidden={!isActive}
            >
              {/*
                `videoClassName` est posé sur un enfant, jamais sur la carte :
                les appelants y passent `relative`, qui écraserait le
                positionnement absolu dont dépend tout le glissement.
              */}
              <div className={`h-full w-full ${videoClassName}`}>
                {item ? (
                  renderVideo(item, index, isActive, scrollToNext, videos.length)
                ) : (
                  <div className="h-full w-full bg-player-surface/40" aria-hidden="true" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </PlaybackEngineContext.Provider>
  );
};

export default FeedDeck;
